<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast"
          :class="`toast--${toast.color}`"
        >
          <div class="toast__body">
            <v-icon
              class="toast__icon"
              :icon="iconFor(toast.color)"
              size="20"
            />
            <span class="toast__message">{{ toast.message }}</span>
            <button class="toast__close" @click="dismiss(toast.id)">
              <v-icon icon="mdi-close" size="16" />
            </button>
          </div>
          <div class="toast__progress">
            <div
              class="toast__progress-bar"
              :style="{ animationDuration: `${toast.timeout}ms` }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useToast } from "@/services/useToast";

const { toasts, dismiss } = useToast();

const iconFor = (color: string) => {
  const icons: Record<string, string> = {
    success: "mdi-check-circle-outline",
    error: "mdi-alert-circle-outline",
    warning: "mdi-alert-outline",
    info: "mdi-information-outline",
  };
  return icons[color] ?? "mdi-information-outline";
};
</script>

<style scoped>
.toast-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 9999;
  pointer-events: none;
}

.toast {
  pointer-events: auto;
  min-width: 300px;
  max-width: 420px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), 0 1.5px 6px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-left: 4px solid;
}

.toast--success {
  border-left-color: var(--cv-success, #2e7d54);
}
.toast--error {
  border-left-color: var(--cv-error, #c62828);
}
.toast--warning {
  border-left-color: var(--cv-warning, #c77700);
}
.toast--info {
  border-left-color: var(--cv-brand, #22288d);
}

.toast__body {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}

.toast__icon {
  flex-shrink: 0;
}

.toast--success .toast__icon {
  color: var(--cv-success, #2e7d54);
}
.toast--error .toast__icon {
  color: var(--cv-error, #c62828);
}
.toast--warning .toast__icon {
  color: var(--cv-warning, #c77700);
}
.toast--info .toast__icon {
  color: var(--cv-brand, #22288d);
}

.toast__message {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: rgba(30, 30, 40, 0.9);
  line-height: 1.4;
}

.toast__close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(30, 30, 40, 0.35);
  padding: 0;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}
.toast__close:hover {
  color: rgba(30, 30, 40, 0.75);
}

.toast__progress {
  height: 3px;
  background: rgba(0, 0, 0, 0.06);
}

.toast__progress-bar {
  height: 100%;
  width: 100%;
  animation: shrink linear forwards;
  transform-origin: left;
}

.toast--success .toast__progress-bar {
  background: var(--cv-success, #2e7d54);
}
.toast--error .toast__progress-bar {
  background: var(--cv-error, #c62828);
}
.toast--warning .toast__progress-bar {
  background: var(--cv-warning, #c77700);
}
.toast--info .toast__progress-bar {
  background: var(--cv-brand, #22288d);
}

@keyframes shrink {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Enter/leave transitions */
.toast-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.toast-leave-active {
  transition: all 0.2s ease-in;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
</style>
